/**********************************************************
Hawaiian Macadamia Nut Services
Alexander Self
4/20/2020
Tree Replant Actions
**********************************************************/
import { ADD_EMPLOYEE, REMOVE_EMPLOYEE, PREPARE_REPLANT, FETCH_REPLANTS, CREATE_REPLANT,
  SET_REPLANT_TO_EDIT, ADD_EMPLOYEE_TO_EDIT, REMOVE_EMPLOYEE_FROM_EDIT, EDIT_REPLANT,
  SET_REPLANT_TO_DELETE, DELETE_REPLANT } from './types';
import { error, success } from './chem_actions';
import { ROOT_URL } from '../../constants';
import { generateID, fillForm, checkApplicationForm } from '../cult_utils';
import { clearForm, fetchFields } from './utils_actions';
import axios from 'axios';
import moment from 'moment';
//const FileSaver = require('file-saver');
const $ = window.$;
/**
* fetchReplants(): Fetches replant logs
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js']
**/
export function fetchReplants() {
  return (dispatch) => {
    axios.get(`${ROOT_URL}/fetchReplants`, {
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
      let { replants } = res.data;
      dispatch({ 
        type: FETCH_REPLANTS,
        payload: replants 
      });
    }).catch((e) => dispatch(error(`There was an error fetching replants ${e}`)))
  }
}
/**
* createReplant(): Creates a replant log 
* @param {Object} data: submission
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js']
**/
export function createReplant({ data, formId }) {
  if (checkApplicationForm(data)) return error('Entire form must be filled.');
  if (data.addedEmployees.length === 0) return error('Must select at least one employee.');
  // Set isComplete based on treesRemaining
  data.isComplete = data.treesRemaining === 0 ? 1 : 0;
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.post(`${ROOT_URL}/createReplant`, { data }
    ).then((res) => {
      $('#preloader').css('display','none');
      clearForm(formId)
      success(res.data.msg);
      let { replant } = res.data;
      dispatch({ 
        type: CREATE_REPLANT,
        payload: replant
      });
    }).catch((e) => dispatch(error(`There was an error creating a replant log`)))
    
  }
}
/**
* editReplant(): Edits a replant log
* @param {Object} data: form data + inventory batch(If new)
* @param {Object} user: logged in user
* @param {Object} oldReplantRecord: record being edited
* @param {Array} inventory: inventory records
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js->tree_replant_table.js']
**/
export function editReplant({ data, user, oldReplantRecord, formId, addedEmployees }) {
  if (checkApplicationForm(data)) return error('Entire form must be filled.');
  if (addedEmployees.length === 0) return error('Must select at least one employee.');
  
  /* Pre-fill missing properties that either need to be updated or referenced */
  data.submitter = user.eid;
  data.editedBy = user.eid;
  data.dateEdited = moment().format('YYYY-MM-DD');
  data.id = oldReplantRecord.id;
  data.replantId = oldReplantRecord.replantId;

  data.treesRemaining = Math.abs(Number(data.treesPlanted) - (Number(oldReplantRecord.treesPlanted) + Number(oldReplantRecord.treesRemaining))); 
  data.treesPerHour = data.treesPlanted && data.hours ? Number((Number(data.treesPlanted) / Number(data.hours)).toFixed(2)) : 0
  data.employees = JSON.stringify(addedEmployees);
  
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.post(`${ROOT_URL}/editReplant`, { data })
      .then((res) => {
        $('#preloader').css('display','none');
        let { replant, msg } = res.data;
        clearForm(formId);
        success(msg);
        dispatch({ type: EDIT_REPLANT, payload: replant });
      }).catch((err) => {
        $('#preloader').css('display','none');
        dispatch(error(`There was an error submitting an inventory request. ${err}`));
      });
  }
}
/**
* deleteReplant(): Deletes a replant log 
* @param {Object} replant: record to be deleted
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js->tree_replant_table.js']
**/
export function deleteReplant(replant) {
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.post(`${ROOT_URL}/deleteReplant`, { id: replant.id, replantId: replant.replantId }
    ).then((res) => {
      $('#preloader').css('display','none');
      success(res.data.msg);
      dispatch({ type: DELETE_REPLANT });
    }).catch((e) => dispatch(error(`There was an error deleting a replant log`)))
    
  }
}
/**
* prepareReplant(): Prepares an replant log for review prior to submission
* @param {Object} user: user data
* @param {Object} form: form data
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js']
**/
export function prepareReplant({ user, form, inventoryTransactions }) {
  let inv = inventoryTransactions.find((i) => Number(i.id) === Number(form.inventoryTransactionId));
  return (dispatch) => {
    axios.get(`${ROOT_URL}/fetchPreviousReplants`, {
      params: { field: form.field, inventoryId: form.inventoryTransactionId },
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
      let { prevReplants } = res.data;
      let treesRemaining = inv ? Math.abs(Number(form.treesPlanted) - inv.quantity) : 0; 
      let replantId = generateID();
      // Keep same replantId: All replants that fall under a inventory transaction have the same unique grouping id
      if (prevReplants.length > 0) {
        replantId = prevReplants[0].replantId;
        treesRemaining = Math.abs(Number(form.treesPlanted) - Math.min(...prevReplants.map((p) => p.treesRemaining)));
      } 
      dispatch({ 
        type: PREPARE_REPLANT,
        payload: { ...form,
          replantId,
          totalTrees: inv ? inv.quantity : 0,
          treesRemaining,
          treesPerHour: form.treesPlanted && form.hours ? Number((Number(form.treesPlanted) / Number(form.hours)).toFixed(2)) : 0,
          submitter: user.eid,
          inventoryTransaction: inv,
          prevReplants
        } 
      });
    }).catch((e) => dispatch(error(`There was an error fetching previous replants`)))
  }
}
/**
* addEmployee(): Adds an employee to a replant log
* @param {Object} val: employee data
* @param {String} id: id of autocomplete input to clear value
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js']
**/
export function addEmployee(val, id) {
  if (id) document.getElementById(id).value = '';
  return {
    type: ADD_EMPLOYEE,
    payload: { eid: val.eid, name: `${val.firstName} ${val.lastName}`, position: val.positionTitle }      
  }
}
/**
* removeEmployee(): Removes an employee from a replant log
* @param {Object} val: employee data
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js']
**/
export function removeEmployee(val) {
  return {
    type: REMOVE_EMPLOYEE,
    payload: val      
  }
}
/**
* setReplantToEdit(): Sets the replant log to be edited
* @param {Object} replant: copy of replant to be edited
* @param {String} formId: Id of form to be pre-filled
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js->tree_replant_table.js']
**/
export function setReplantToEdit({replant, formId}) {
 return (dispatch) => {
  dispatch(fetchFields(replant.location));
  setTimeout(() => {
    fillForm(formId, replant);
  }, 200);
  dispatch({ type: SET_REPLANT_TO_EDIT, payload: replant}); 
 }
}
/**
* addEmployee(): Adds an employee to a replant log
* @param {Object} val: employee data
* @param {String} id: id of autocomplete input to clear value
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js->tree_replant_table.js']
**/
export function addEmployeeToEdit(val, id) {
  if (id) document.getElementById(id).value = '';
  return {
    type: ADD_EMPLOYEE_TO_EDIT,
    payload: { eid: val.eid, name: `${val.firstName} ${val.lastName}`, position: val.positionTitle }      
  }
}
/**
* removeEmployee(): Removes an employee from a replant log
* @param {Object} val: employee data
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js->tree_replant_table.js']
**/
export function removeEmployeeFromEdit(val) {
  return {
    type: REMOVE_EMPLOYEE_FROM_EDIT,
    payload: val      
  }
}

/**
* setReplantToDelete(): Sets the replant log to be deleted
* @param {Object} replant: copy of replant to be deleted
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_replant.js->tree_replant_table.js']
**/
export function setReplantToDelete(replant) {
  return {
   type: SET_REPLANT_TO_DELETE,
   payload: replant
  }
 }

















/* END */


// WEBPACK FOOTER //
// ./src/cultivation/actions/tree_replant_actions.js